<template>
    <!-- MYAC-802 — clarifying questions (chat asks, user taps propositions).
         Single question: tapping a reply submits it directly.
         Several questions: one under the other, each reply picked in place (the
         focus moves on to the next unanswered one), ONE combined submit once every
         question is answered.
         Answered: collapses to a read-only line; the chosen answers are visible as
         the user's own message right below, so nothing is repeated here.
         Redesign (MYAC-1522) — written in the letter's shape, not a boxed card: the
         question in bold like the onboarding questions (PersonOnboardingChat), its
         replies as the letter's arrow lines (ChatOpening's `.opening-reply`). -->
    <div class="flex flex-col items-start">
        <!-- Redesign: once answered, a quiet line rather than a boxed card. -->
        <div v-if="group.answered" class="flex items-center gap-2 text-sm text-surface-500 px-1">
            <i class="pi pi-check text-primary text-xs" />
            <span>{{ ct('chat.questions.answered') }}</span>
            <span class="truncate">{{ answeredSummary }}</span>
        </div>

        <!-- MYAC-1269 — a reader may not answer: the replies stay, disabled, and the
             reason is said in place (disabled-with-reason rule). -->
        <p v-else-if="chatbotStore.isReadOnlyConversation" class="m-0 px-1 text-xs text-surface-500"
            data-testid="question-cards-read-only">
            <i class="pi pi-lock mr-1" aria-hidden="true" />{{ ct('chat.shared.read_only_hint') }}
        </p>
        <div v-if="!group.answered" ref="root" class="answer-text w-full max-w-2xl flex flex-col gap-5 px-1"
            data-testid="chat-questions">
            <div v-for="(q, qi) in questions" :key="qi" class="flex flex-col" :data-question="qi">
                <span v-if="q.header && !isSingle" class="question-header">{{ q.header }}</span>
                <p class="question-text">{{ q.question }}</p>
                <QuestionOptions :question="q" :qi="qi" />
            </div>

            <!-- Single-question groups submit on a reply's click; only several
                 questions (or a free-text answer) need the explicit combined submit. -->
            <div v-if="needsSubmitButton">
                <Button size="small" :label="ct('chat.questions.submit')"
                    :disabled="disabled || !isComplete" icon="pi pi-send"
                    @click="submit()" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, nextTick, reactive, ref } from 'vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import type { ChatQuestion } from '@/models/ChatbotEvent'
import { useChatbotStore, type QuestionGroup } from '@/stores/chatbot'
import { useCardLocale } from '@/composables/useCardLocale'
import { useFeaturesStore } from '@/stores/features'

const props = defineProps<{ group: QuestionGroup }>()

const { ct } = useCardLocale(computed(() => props.group.group.language))
const chatbotStore = useChatbotStore()
const featuresStore = useFeaturesStore()

// Per-question local answer state, keyed by question index.
const answers = reactive<Record<number, string>>({})
const freeTexts = reactive<Record<number, string>>({})
const freeTextOpen = reactive<Record<number, boolean>>({})
const root = ref<HTMLElement | null>(null)

// Credit-gated ($$$): answering fires a paid chat_message, same as typing —
// blocked tenants get the same disabled state as the input/retry/suggestions.
// MYAC-1269 — a reader of a shared conversation may not answer either (the
// backend refuses the turn as `read_only`; this says so before the click).
const disabled = computed(() =>
    props.group.answered || chatbotStore.isStreaming || chatbotStore.isLoading
    || featuresStore.isCreditsBlocked || chatbotStore.isReadOnlyConversation)

const questions = computed(() => props.group.group.questions)
const isSingle = computed(() => questions.value.length === 1)

/**
 * MYAC-883 — a question the SERVICE authored (the register-refresh scoping cards)
 * ships i18n keys alongside its English labels, because it cannot know the user's
 * language. Model-authored questions carry none and are already in-language.
 * Falls back to the wire text whenever a key is missing or untranslated.
 */
function optionLabel(qi: number, opt: string): string {
    const keys = questions.value[qi].option_keys
    if (!keys) return opt
    const key = keys[questions.value[qi].options.indexOf(opt)]
    if (!key) return opt
    // Resolve and compare, never te(): te() misses the fallback locale, and the
    // runtime locale is the raw browser tag (`fr-FR`), so it said « no French »
    // for keys t() resolves fine (front CLAUDE.md).
    const path = `chat.questions.option.${key}`
    const label = ct(path)
    return label === path ? opt : label
}

/** The effective answer for one question: picked option, else non-empty free text. */
function answerFor(qi: number): string {
    if (answers[qi]) return answers[qi]
    if (freeTextOpen[qi]) return (freeTexts[qi] ?? '').trim()
    return ''
}

const isComplete = computed(() =>
    questions.value.every((_, qi) => answerFor(qi) !== ''))

const needsSubmitButton = computed(() =>
    !isSingle.value || Object.values(freeTextOpen).some(Boolean))

const answeredSummary = computed(() =>
    questions.value.map(q => q.header || q.question).join(' · '))

/**
 * The reply that leads: the one picked, else the recommended one while nothing
 * is picked (the letter's « is-first » line, the one to press).
 */
function isLead(qi: number, opt: string): boolean {
    return !answers[qi] && questions.value[qi].recommended === opt
}

function pickOption(qi: number, opt: string) {
    answers[qi] = opt
    freeTextOpen[qi] = false
    // One question, one tap: submit straight away (mirrors change-control's
    // ClarificationCard). Several: the focus moves on to the next unanswered
    // question, and the combined submit waits for the last one.
    if (isSingle.value) {
        submit()
        return
    }
    const next = questions.value.findIndex((_, i) => !answerFor(i))
    if (next !== -1) {
        void nextTick(() => {
            root.value?.querySelector<HTMLButtonElement>(`[data-question="${next}"] .opening-reply`)?.focus()
        })
    }
}

function toggleFreeText(qi: number) {
    freeTextOpen[qi] = !freeTextOpen[qi]
    if (freeTextOpen[qi]) delete answers[qi]
}

function submitIfComplete() {
    if (isComplete.value && !disabled.value) submit()
}

/**
 * Compose ONE visible user message from all answers — the model reads plain
 * conversation, no sentinel protocol. "Header: answer" per line; a bare answer
 * when the group has a single question with no header.
 */
function submit() {
    if (disabled.value || !isComplete.value) return
    const lines = questions.value.map((q, qi) => {
        const label = q.header || q.question
        // Send what the user actually SAW: on a service-authored card the button is
        // localized, and an English sentence in a French transcript reads like a bug.
        // The chatbot's normalizers match the markers of both languages.
        const answer = answers[qi] ? optionLabel(qi, answers[qi]) : answerFor(qi)
        return isSingle.value && !q.header ? answer : `${label}: ${answer}`
    })
    chatbotStore.sendMessage(lines.join('\n'))
}

/** The replies of one question, as the letter's arrow lines, and its free text —
 *  one per question. Local functional component so all state stays in this setup. */
const QuestionOptions = defineComponent({
    props: { question: { type: Object, required: true }, qi: { type: Number, required: true } },
    setup(p) {
        return () => {
            const q = p.question as ChatQuestion
            const qi = p.qi
            const line = (label: string, icon: string, opts: { lead?: boolean, picked?: boolean },
                attrs: Record<string, unknown>) => h('button', {
                type: 'button',
                class: ['opening-reply', { 'is-first': opts.lead, 'is-picked': opts.picked }],
                disabled: disabled.value,
                ...attrs,
            }, [h('span', label), h('i', { class: `pi ${icon} text-sm`, 'aria-hidden': 'true' })])
            const children = [
                h('div', { class: 'flex flex-col border-t border-surface-200' }, [
                    ...q.options.map(opt => {
                        const picked = answers[qi] === opt
                        return line(optionLabel(qi, opt), picked ? 'pi-check' : 'pi-arrow-right',
                            { lead: isLead(qi, opt), picked }, {
                                key: opt,
                                'aria-pressed': isSingle.value ? undefined : picked,
                                onClick: () => pickOption(qi, opt),
                            })
                    }),
                    line(ct('chat.questions.other'), 'pi-pencil', { picked: !!freeTextOpen[qi] }, {
                        key: '__other',
                        'aria-expanded': !!freeTextOpen[qi],
                        onClick: () => toggleFreeText(qi),
                    }),
                ]),
            ]
            if (freeTextOpen[qi]) {
                children.push(h(InputText, {
                    modelValue: freeTexts[qi] ?? '',
                    'onUpdate:modelValue': (v: string | undefined) => { freeTexts[qi] = v ?? '' },
                    class: 'mt-2 w-full',
                    size: 'small',
                    placeholder: ct('chat.questions.free_text_placeholder'),
                    'aria-label': q.question,
                    disabled: disabled.value,
                    onKeydown: (e: KeyboardEvent) => {
                        if (e.key === 'Enter') { e.preventDefault(); submitIfComplete() }
                    },
                }))
            }
            return h('div', children)
        }
    },
})
</script>
